import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { FiSearch, FiChevronDown } from 'react-icons/fi';
import { useApi } from '../useApi';
import { useAuth } from '../AuthContext';
import './TournamentSelectorModal.css';

/**
 * The competition picker, shared by the Leaderboard, Insights and the
 * scorecard control centre.
 *
 * ONE LIST, SECTIONED BY WHETHER YOU ARE IN IT — not a Mine/All filter. A
 * filter is a mode you have to discover, remember, and pay a tap for every
 * time you want the other answer; the question here ("what am I playing, and
 * what else is the society up to") wants both on screen at once. So the
 * competitions you are entered in come first and are always visible, and the
 * rest sit behind one disclosure: present, but quiet.
 *
 * A row also says what STATE the competition is in. It used to be name + date
 * alone, which is most of why a list of ten read as noise in a busy society —
 * nothing on it said which one was worth opening. `status`, `latest_round` and
 * `round_count` were already in the payload (`Tournament.summarize`) and were
 * being thrown away.
 *
 * THE DEFAULT FETCH IS TWO CALLS, and that is the point. Ten-by-date across a
 * society with several groups out can contain none of yours, and sectioning a
 * starved list still shows you nothing — so `?mine=true` is asked separately
 * and merged. Both parameters already existed on `GET /tournaments`.
 *
 * NO YOURS MEANS NO SECTIONS: a system admin with no linked Player gets `[]`
 * from `?mine=true` by design, as does a member entered in nothing yet. Either
 * way the sheet falls back to the flat, unheaded list that shipped before this.
 */

const ALL_LIMIT = 10;
const MINE_LIMIT = 8;
const SEARCH_LIMIT = 50;

const fmtDate = (d) => {
    if (!d) return '';
    const parsed = new Date(d);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** The pill. `null` for a competition nobody has teed off in — the date is
 *  already saying everything there is to say about it. */
export const statusPill = (t) => {
    if (t?.status === 'in_progress') {
        const of = t.round_count || 0;
        return (of > 1 && t.latest_round)
            ? { text: `Round ${t.latest_round} of ${of}`, tone: 'live' }
            : { text: 'Live', tone: 'live' };
    }
    if (t?.status === 'complete') return { text: 'Complete', tone: 'complete' };
    return null;
};

const TournamentRow = ({ t, isSelected, onPick }) => {
    const pill = statusPill(t);
    const meta = [fmtDate(t.date), t.location].filter(Boolean).join(' · ');
    return (
        <button
            type="button"
            className={`tsm-row${isSelected ? ' selected' : ''}`}
            onClick={() => onPick(t.id.toString())}
        >
            <span className={`tsm-dot${t.status === 'in_progress' ? '' : ' placeholder'}`} />
            <span className="tsm-row-body">
                <span className="tsm-name">{t.name}</span>
                <span className="tsm-meta">{meta}</span>
            </span>
            {pill && <span className={`tsm-pill ${pill.tone}`}>{pill.text}</span>}
        </button>
    );
};

const TournamentSelectorModal = ({ isOpen, onClose, onSelect, selectedId }) => {
    const { authenticatedFetch } = useApi();
    const { currentSociety } = useAuth();
    const societyId = currentSociety?.id;

    const [searchTerm, setSearchTerm] = useState('');
    const [query, setQuery] = useState('');       // debounced
    const [showOthers, setShowOthers] = useState(false);

    const fetchList = async (qs) => {
        const res = await authenticatedFetch(`/tournaments?${qs}`);
        return res.ok ? res.json() : [];
    };

    // Deliberately the SAME key and URL the host pages already use, so opening
    // the sheet costs no request at all — it reads their cache. (The old modal
    // fetched `/tournaments?limit=10` on its own mount while its parent fetched
    // the identical URL through React Query: every page load asked twice.)
    const { data: all = [], isLoading: allLoading } = useQuery(
        ['tournaments', societyId],
        () => fetchList(`limit=${ALL_LIMIT}`),
        { enabled: !!societyId }
    );

    const { data: mine = [], isLoading: mineLoading } = useQuery(
        // Keyed UNDER the society, not beside it, so the invalidations the rest
        // of the app already fires — `['tournaments', societyId]` after a
        // create, edit or delete — sweep these two as well.
        ['tournaments', societyId, 'mine'],
        () => fetchList(`mine=true&limit=${MINE_LIMIT}`),
        { enabled: !!societyId }
    );

    const { data: found = [], isFetching: searching } = useQuery(
        ['tournaments', societyId, 'search', query],
        () => fetchList(`q=${encodeURIComponent(query)}&limit=${SEARCH_LIMIT}`),
        { enabled: !!societyId && !!query, keepPreviousData: true }
    );

    // Debounce, and reset the sheet's own state each time it closes.
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            setQuery('');
            setShowOthers(false);
            return;
        }
        const timer = setTimeout(() => setQuery(searchTerm.trim()), 400);
        return () => clearTimeout(timer);
    }, [searchTerm, isOpen]);

    const isSearching = !!query;

    const { yours, others } = useMemo(() => {
        if (isSearching) {
            // Search cuts across the split — you asked for these — but the
            // results stay sectioned so you can still see which hits are yours.
            return {
                yours: found.filter((t) => t.is_user_signed_up),
                others: found.filter((t) => !t.is_user_signed_up),
            };
        }
        // A competition in progress is the overwhelmingly likely target, so it
        // floats; everything else keeps the server's date order.
        const ranked = [...mine].sort(
            (a, b) => (a.status === 'in_progress' ? 0 : 1) - (b.status === 'in_progress' ? 0 : 1));
        const mineIds = new Set(mine.map((t) => t.id));
        return { yours: ranked, others: all.filter((t) => !mineIds.has(t.id)) };
    }, [isSearching, found, mine, all]);

    if (!isOpen) return null;

    const pick = (id) => { onSelect(id); onClose(); };
    const isSel = (t) => t.id.toString() === selectedId?.toString();
    const rowsFor = (list) => list.map((t) => (
        <TournamentRow key={t.id} t={t} isSelected={isSel(t)} onPick={pick} />
    ));

    // ALWAYS SECTIONED, even with nothing of your own. This used to be
    // `yours.length > 0`, which skipped the entire block — both headings AND
    // the disclosure — so a member who had not played yet got a bare,
    // unlabelled list of every competition in the society, flat and fully
    // expanded. Nothing on screen said whose competitions these were, and it
    // was the longest the list could possibly be.
    const hasYours = yours.length > 0;
    // With nothing of your own there is nothing else to show, so the other
    // section opens by itself rather than presenting an empty sheet.
    const othersOpen = isSearching || showOthers || !hasYours;
    const loading = isSearching ? searching && found.length === 0 : (allLoading || mineLoading);
    const empty = !loading && yours.length === 0 && others.length === 0;

    return (
        <div className="tsm-overlay" onClick={onClose}>
            <div className="tsm-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="tsm-title">Select Competition</div>

                <div className="tsm-search">
                    <FiSearch className="tsm-search-icon" size={16} />
                    <input
                        type="text"
                        placeholder="Find a competition..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="tsm-list">
                    {loading && <div className="tsm-status">{isSearching ? 'Searching…' : 'Loading…'}</div>}
                    {empty && <div className="tsm-status">No competitions found.</div>}

                    {!loading && !empty && (
                        <>
                            <div className="tsm-section-label">Yours</div>
                            {hasYours ? rowsFor(yours) : (
                                <div className="tsm-section-empty">
                                    Nothing you&apos;re entered in yet.
                                </div>
                            )}

                            {others.length > 0 && (
                                isSearching ? (
                                    <>
                                        <div className="tsm-section-label">Elsewhere in the society</div>
                                        {rowsFor(others)}
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            className="tsm-disclosure"
                                            onClick={() => setShowOthers((v) => !v)}
                                            aria-expanded={showOthers}
                                        >
                                            <span className="tsm-section-label">Elsewhere in the society</span>
                                            <span className="tsm-disclosure-count">{others.length}</span>
                                            <FiChevronDown
                                                className={`tsm-disclosure-chev${showOthers ? ' open' : ''}`}
                                                size={16}
                                            />
                                        </button>
                                        {othersOpen && rowsFor(others)}
                                    </>
                                )
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TournamentSelectorModal;
